/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { InputActionMeta, SelectInstance } from "react-select";
import Select from "react-select";
import { api } from "../../api";
import type { AddressLookupDetailsDto } from "@/api/types";
import "./index.css";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { telemetry } from "@/telemetry";

interface Props {
  name: string;
  value: string;
  placeholder: string;
  onSelect: (data: AddressLookupDetailsDto) => unknown;
  onBlur: (e: any) => unknown;
  onSearch?: () => unknown;
}

interface State {
  searchText: string;
  containerId: string;
  selectedAddressId?: string;
}

export const AddressLookupSelect = ({
  name,
  onSelect,
  onSearch,
  placeholder,
  value,
}: Props) => {
  const ref = useRef<SelectInstance>(null);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [state, setState] = useState<State>({
    containerId: "",
    searchText: "",
  });

  const query = useMemo(
    () => ({
      text: state.searchText,
      container: state.containerId,
    }),
    [state.searchText, state.containerId],
  );

  const lookupQuery = useApiQuery({
    queryKey: ["address-lookups", query],
    queryFn: async () => {
      const elapsed = telemetry.startTimer();
      const data = await api.getAddressLookups(query);

      telemetry.trackEvent("address_lookup_searched", {
        result_count: data.length,
        duration_ms: elapsed(),
        query_length: query.text.length,
      });
      onSearch?.();

      return data;
    },
    enabled: !!state.searchText || !!state.containerId,
  });

  const detailsQuery = useApiQuery({
    queryKey: ["address-lookup-details", state.selectedAddressId],
    queryFn: async () => {
      const elapsed = telemetry.startTimer();

      try {
        const data = await api.getAddressLookupDetails(
          state.selectedAddressId!,
        );
        telemetry.trackEvent("address_lookup_selected", {
          result: "ok",
          duration_ms: elapsed(),
        });
        return data;
      } catch (error) {
        telemetry.trackEvent("address_lookup_selected", {
          result: "error",
          duration_ms: elapsed(),
        });
        throw error;
      }
    },
    enabled: !!state.selectedAddressId,
  });

  useEffect(() => {
    const data = detailsQuery.data;

    if (data) onSelect(data);
  }, [detailsQuery.data]);

  useEffect(() => {
    if (state.containerId) {
      setOpen(true);
      ref.current?.focus();
    }
  }, [state.containerId]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const isLoading = lookupQuery.isLoading || detailsQuery.isLoading;

  const handleSelectChange = (option: any) => {
    if (option.type === "Address") {
      setState({
        ...state,
        containerId: "",
        selectedAddressId: option.value,
      });
      setOpen(false);
    } else {
      setState({
        searchText: inputValue,
        containerId: option.value,
        selectedAddressId: undefined,
      });
    }
  };

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    setInputValue(newValue);
    if (newValue) setState((prev) => ({ ...prev, searchText: newValue }));
    if (meta.action === "menu-close") setOpen(false);
  };

  const handleMenuOpen = () => setOpen(true);

  const options = useMemo(
    () =>
      lookupQuery.data?.map((lookup) => ({
        label: lookup.text,
        value: lookup.id,
        type: lookup.type,
      })) || [],
    [lookupQuery.data],
  );

  return (
    <Select
      ref={ref}
      key={`${name}-${value}`}
      classNamePrefix="select"
      isSearchable
      isLoading={isLoading}
      options={options}
      inputValue={inputValue}
      closeMenuOnSelect={false}
      onMenuOpen={handleMenuOpen}
      onInputChange={handleInputChange}
      onChange={handleSelectChange}
      menuIsOpen={isOpen}
      placeholder={placeholder || value}
    />
  );
};
